<template>
    <div id="reviewCreativeVue">
        <div
            v-if="isOpen"
            class="modal slide-in-top d-block"
            id="reviewCreativeModal"
            tabindex="-1"
            role="dialog"
            aria-hidden="false"
            @click.self="close"
        >
            <div
                class="modal-dialog modal-dialog-centered"
                :class="modalSizeClass"
                role="document"
            >
                <update-creative-carousel
                    v-if="isAdvertiser"
                    :creative-ids="creativeIds"
                    :order-id="orderId"
                    :order-status-date="orderStatusDate"
                    @close="close"
                    @saved="onSaved"
                />
                <review-creative-carousel
                    v-else
                    :creative-ids="creativeIds"
                    :order-id="orderId"
                    :is-order="isOrder"
                    @close="close"
                />
            </div>
        </div>
        <div v-if="isOpen" class="modal-backdrop fade show" @click="close"></div>
    </div>
</template>

<script>
export default {
    name: 'ReviewCreativeModal',

    props: {
        /**
         * The user's role (advertiser, team, or admin)
         */
        userRole: {
            type: String,
            default: 'advertiser',
        },
    },

    data() {
        return {
            isOpen: false,
            creativeIds: [],
            orderId: null,
            isOrder: false,
            orderStatusDate: null,
        };
    },

    computed: {
        /**
         * Check if the user is an advertiser.
         */
        isAdvertiser() {
            return this.userRole === 'advertiser';
        },

        /**
         * Modal size class based on user role.
         * Advertisers need more space for the update form.
         */
        modalSizeClass() {
            return this.isAdvertiser ? 'modal-lg' : 'modal-md';
        },
    },

    mounted() {
        this._openHandler = this.open.bind(this);
        this._closeHandler = this.close.bind(this);

        // Listen for Vue Bus events
        Bus.$on('openReviewCreative', this._openHandler);
        Bus.$on('closeReviewCreative', this._closeHandler);

        // Bridge Livewire events to Vue
        this.setupLivewireBridge();
    },

    beforeDestroy() {
        Bus.$off('openReviewCreative', this._openHandler);
        Bus.$off('closeReviewCreative', this._closeHandler);
    },

    methods: {
        /**
         * Setup bridge between Livewire events and Vue.
         * This allows Livewire components to trigger the Vue modal.
         */
        setupLivewireBridge() {
            if (typeof window.Livewire !== 'undefined') {
                this.registerLivewireListeners();
            } else {
                document.addEventListener('livewire:init', () => {
                    this.registerLivewireListeners();
                });
            }
        },

        /**
         * Register Livewire event listeners that forward to Vue.
         */
        registerLivewireListeners() {
            // Listen for Livewire 'openReviewCreative' event
            window.Livewire.on('openReviewCreative', (data) => {
                // Livewire 3 passes data as an array
                if (Array.isArray(data)) {
                    const [creativeIds, orderId, isOrder] = data;
                    this.open(creativeIds, orderId, isOrder || false);
                } else if (data && typeof data === 'object') {
                    // Handle object format
                    this.open(
                        data.creativeIds || data.creative_ids || [],
                        data.orderId || data.order_id,
                        data.isOrder || data.is_order || false,
                    );
                }
            });

            // Listen for Livewire 'closeReviewCreative' event
            window.Livewire.on('closeReviewCreative', () => {
                this.close();
            });
        },

        /**
         * Open the modal with the given creative IDs and order ID.
         *
         * @param {number[]} creativeIds - Array of creative IDs to review
         * @param {number} orderId - The order ID
         * @param {boolean} isOrder - Whether this is an order-level review
         * @param {string|null} orderStatusDate - Optional order status date
         */
        open(creativeIds, orderId, isOrder = false, orderStatusDate = null) {
            this.creativeIds = creativeIds;
            this.orderId = orderId;
            this.isOrder = isOrder;
            this.orderStatusDate = orderStatusDate;
            this.isOpen = true;

            // Add modal-open class to body to prevent scrolling
            document.body.classList.add('modal-open');
        },

        /**
         * Close the modal and emit events.
         */
        close() {
            // Emit order-specific close event
            if (this.orderId) {
                Bus.$emit('orderReviewClosed' + this.orderId);
            }

            // Reset state
            this.orderId = null;
            this.creativeIds = [];
            this.isOrder = false;
            this.orderStatusDate = null;
            this.isOpen = false;

            // Remove modal-open class from body
            document.body.classList.remove('modal-open');

            // Emit generic close event
            Bus.$emit('creativeReviewClosed');
        },

        /**
         * Handle saved event from carousel.
         */
        onSaved() {
            // Could refresh data or close modal depending on requirements
            Bus.$emit('refreshOrdersTable');
        },
    },
};
</script>

<style scoped>
.modal {
    background-color: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
    z-index: 1040;
}

.modal {
    z-index: 1050;
}

.slide-in-top {
    animation: slideInTop 0.3s ease-out;
}

@keyframes slideInTop {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
